import { Component, OnDestroy, OnInit } from '@angular/core';
import { DwComponent, DwComponentType } from '@devwareapps/devware-cap';
import { of, Subject } from 'rxjs';
import { catchError, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { DocumentItemEntity } from '../../../../../meta-data/app-meta-data.service';
import { DocumentsRepositoryService } from '../../../services/documents-repository.service';
import { StructuredDocumentViewerModel } from '../../models/structured-document-viewer.model';
import { StructuredDocumentViewerService } from '../../services/structured-document-viewer.service';

@DwComponent({
  componentType: DwComponentType.pageComponent,
  key: 'far-aim-viewer-page',
  name: 'FAR/AIM Viewer Page',
  isGlobal: true
})
@Component({
  selector: 'app-far-aim-viewer-page',
  templateUrl: './far-aim-viewer-page.component.html',
  styleUrls: ['./far-aim-viewer-page.component.scss']
})
export class FarAimViewerPageComponent implements OnInit, OnDestroy {
  private readonly testDocumentItemId = 1042;
  private readonly destroy$ = new Subject<void>();

  loading = false;
  // ponytail: static placeholder shape (3 + 6 rows echoes Subchapter A/B) — template can't hold array literals
  readonly skeletonGroups = [[0, 1, 2], [0, 1, 2, 3, 4, 5]];
  documentItem?: DocumentItemEntity;
  viewerData: StructuredDocumentViewerModel | null = null;
  errorMessage?: string;

  constructor(
    private documentsRepositoryService: DocumentsRepositoryService,
    private structuredDocumentViewerService: StructuredDocumentViewerService
  ) { }

  ngOnInit(): void {
    this.loadDocument();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadDocument(): void {
    this.loading = true;
    this.errorMessage = undefined;
    this.viewerData = null;

    this.documentsRepositoryService.loadDocumentItem(this.testDocumentItemId)
      .pipe(
        switchMap(documentItem => {
          this.documentItem = documentItem;
          return this.structuredDocumentViewerService
            .getStructuredDocumentViewer(documentItem.DocumentItemId)
            .pipe(
              catchError(error => {
                this.errorMessage = this.getStructuredViewerErrorMessage(error);
                return of(null);
              })
            );
        }),
        catchError(() => {
          this.documentItem = undefined;
          this.errorMessage = `Unable to load FAR/AIM test document ${this.testDocumentItemId}.`;
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(viewerData => {
        if (!viewerData) {
          return;
        }

        if (viewerData.DocumentVersionId) {
          this.viewerData = viewerData;
          this.errorMessage = undefined;
          return;
        }

        if (!this.errorMessage) {
          this.errorMessage = 'Structured document content is not available for this FAR/AIM document yet.';
        }
      });
  }

  private getStructuredViewerErrorMessage(error: any): string {
    if (error?.status === 404) {
      return 'Structured document content has not been loaded for this FAR/AIM document yet.';
    }

    return 'There was a problem loading the FAR/AIM content. Please try again later.';
  }
}
